import { apiRequest } from '../../config/api';
import { searchTypeToEntityType } from './prospectingEntityType';
import { mapCanonicalFiltersToUi } from './prospectingAssistantFilters';
import { canonicalizeIndustryValues } from './industryCanonicalization';
import { buildBrandfetchLogoUrl } from './brandfetchLogo';

export { searchTypeToEntityType };

export const PROSPECTING_PREVIEW_LIMIT = 50;
export const PROSPECTING_SAVED_LIST_LIMIT = 20;

const COUNTRY_TO_CODE = {
  denmark: 'DK',
  dk: 'DK',
  sweden: 'SE',
  se: 'SE',
  norway: 'NO',
  no: 'NO',
  germany: 'DE',
  de: 'DE',
  spain: 'ES',
  es: 'ES',
  'united kingdom': 'GB',
  uk: 'GB',
  britain: 'GB',
  england: 'GB',
  'united states': 'US',
  usa: 'US',
  us: 'US',
  'united states of america': 'US',
  netherlands: 'NL',
  nl: 'NL',
};

const COUNTRY_CODE_TO_NAME = {
  DK: 'Denmark',
  SE: 'Sweden',
  NO: 'Norway',
  DE: 'Germany',
  ES: 'Spain',
  GB: 'United Kingdom',
  US: 'United States',
  NL: 'Netherlands',
};

const EMPLOYEE_RANGE_BOUNDS = {
  '1-10': { gte: 1, lte: 10 },
  '11-50': { gte: 11, lte: 50 },
  '51-200': { gte: 51, lte: 200 },
  '201-1000': { gte: 201, lte: 1000 },
  '1000+': { gte: 1000 },
};

const SENIORITY_MAP = {
  'c-level': 'c_suite',
  'c level': 'c_suite',
  'c-suite': 'c_suite',
  chief: 'c_suite',
  vp: 'vp',
  director: 'director',
  head: 'head',
  manager: 'manager',
  senior: 'senior_ic',
  ic: 'ic',
};

const FUNCTION_MAP = {
  marketing: 'marketing',
  sales: 'sales',
  product: 'product',
  engineer: 'engineering',
  engineering: 'engineering',
  design: 'design',
  operation: 'operations',
  operations: 'operations',
  content: 'content',
};

const AVATAR_COLORS = [
  'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300',
  'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
];

export function buildLeadPreviewRequest(search, limit = PROSPECTING_PREVIEW_LIMIT) {
  const entityType = searchTypeToEntityType(search.type);
  const filters = buildCanonicalFiltersFromUiFilters(search.type, search.filters || []);

  return {
    entity_type: entityType,
    filters,
    limit,
    sort: { field: 'best_match', direction: 'desc' },
  };
}

export async function loadProspectingPreview(search, { signal, limit = PROSPECTING_PREVIEW_LIMIT } = {}) {
  const request = buildLeadPreviewRequest(search, limit);

  const previewResponse = await apiRequest('/prospecting/preview', {
    method: 'POST',
    body: JSON.stringify(request),
    signal,
  });
  const preview = await parseJsonResponse(previewResponse, 'Could not load prospecting preview');

  return { preview, request };
}

export async function sendProspectingAssistantMessage({
  entityType,
  userMessage,
  currentFilters = {},
  conversation = [],
}) {
  try {
    const response = await apiRequest('/prospecting/assistant/message', {
      method: 'POST',
      body: JSON.stringify({
        entity_type: entityType,
        user_message: userMessage,
        current_filters: currentFilters,
        conversation,
      }),
    });

    if (!response.ok) {
      const message = await readErrorMessage(
        response,
        'I could not update your prospect filters right now. Please try again.'
      );
      throw new Error(message);
    }

    try {
      return await response.json();
    } catch {
      throw new Error(
        'I received an invalid assistant response. Please try again.'
      );
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('I could not reach the prospecting assistant right now.');
  }
}

export async function analyzeGuidedProspectingSetup({ setup = {}, fallbackIcp }) {
  const fallbackPayload = buildFallbackGuidedIcpPayload(fallbackIcp);

  try {
    const response = await apiRequest('/prospecting/assistant/guided-setup', {
      method: 'POST',
      body: JSON.stringify({
        for_who: setup.forWho || null,
        business_type_id: setup.businessTypeId || null,
        business_type_label: setup.businessTypeLabel || null,
        business_description: setup.businessDescription || '',
        goal_id: setup.goalId || null,
        goal_label: setup.goalLabel || null,
        fallback_icp: fallbackPayload,
      }),
    });

    if (!response.ok) {
      const message = await readErrorMessage(
        response,
        'I could not analyze your guided setup right now. Please try again.'
      );
      throw new Error(message);
    }

    const payload = await parseJsonResponse(
      response,
      'I could not analyze your guided setup right now. Please try again.'
    );
    return mapGuidedSetupResponseToIcp(payload);
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('I could not analyze your guided setup right now. Please try again.');
  }
}

export function buildCanonicalFiltersFromUiFilters(searchType, uiFilters = []) {
  const entityType = searchTypeToEntityType(searchType);
  const filters = {};

  for (const filter of uiFilters || []) {
    applyPrototypeFilter(filters, filter, entityType);
  }

  return filters;
}

export async function createProspectingSavedList({ name, request, previewResponse = null, signal }) {
  const payload = {
    name,
    preview_request: request,
  };
  if (previewResponse) {
    payload.preview_response = previewResponse;
  }

  const response = await apiRequest('/prospecting/lists', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });

  return parseJsonResponse(response, 'Could not save prospecting list');
}

export async function loadProspectingSavedLists({ signal } = {}) {
  const response = await apiRequest('/prospecting/lists', { signal });
  return parseJsonResponse(response, 'Could not load saved lists');
}

export async function loadProspectingSavedList(listId, { signal } = {}) {
  const response = await apiRequest(`/prospecting/lists/${listId}`, { signal });
  return parseJsonResponse(response, 'Could not load saved list');
}

export async function archiveProspectingSavedList(listId) {
  const response = await apiRequest(`/prospecting/lists/${listId}/archive`, {
    method: 'POST',
  });
  return parseJsonResponse(response, 'Could not archive saved list');
}

export async function addProspectingSavedListField(
  listId,
  {
    label,
    fieldType = 'text',
    kind = 'manual',
    prompt = null,
    runNow = true,
  }
) {
  const payload = {
    label,
    field_type: fieldType,
    kind,
    run_now: runNow,
  };
  if (prompt) {
    payload.prompt = prompt;
  }

  const response = await apiRequest(`/prospecting/lists/${listId}/fields`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  return parseJsonResponse(response, 'Could not add column');
}

export async function runProspectingSavedListEnrichment(listId, fieldId) {
  const response = await apiRequest(
    `/prospecting/lists/${listId}/fields/${fieldId}/enrich`,
    { method: 'POST' }
  );
  return parseJsonResponse(response, 'Could not start enrichment');
}

export async function updateProspectingSavedListValue(listId, recordId, fieldId, value) {
  const response = await apiRequest(
    `/prospecting/lists/${listId}/records/${recordId}/values/${fieldId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }
  );

  return parseJsonResponse(response, 'Could not update cell');
}

export async function loadProspectingSavedListCsv(listId) {
  const response = await apiRequest(`/prospecting/lists/${listId}/export.csv`);
  if (response.ok) {
    return response.blob();
  }

  const message = await readErrorMessage(response, 'Could not export saved list');
  throw new Error(message);
}

export async function createProspectingCrmCompanyFromRecord(record) {
  const payload = buildCrmCompanyPayloadFromSavedListRecord(record);
  if (!payload) {
    throw new Error('This row does not include a company to acquire');
  }

  const response = await apiRequest('/crm/companies/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  return parseJsonResponse(response, 'Could not acquire company');
}

export function buildCrmCompanyPayloadFromSavedListRecord(record) {
  const snapshot = record?.snapshot || {};
  const fields = snapshot.fields || {};
  const company = snapshot.company || {};
  const isCompanyRow = snapshot.entity_type === 'company' || record?.entity_type === 'company';
  const name = [
    company.display_name,
    isCompanyRow ? snapshot.display_name : null,
    fields.company_name,
  ].map(cleanText).find(Boolean);

  if (!name || name === 'Unknown company') return null;

  const payload = {
    name,
    custom_fields: {
      prospecting_source: 'saved_list',
      prospecting_record_id: record?.entity_id || snapshot.id || null,
      prospecting_company_id: company.id || (isCompanyRow ? snapshot.id : null),
    },
  };

  addOptionalPayloadField(payload, 'website', normalizeWebsiteUrl(
    company.domain || (isCompanyRow ? snapshot.links?.website_url : null)
  ));
  addOptionalPayloadField(payload, 'industry', company.industry || fields.company_industry || fields.industry || snapshot.subtitle);
  addOptionalPayloadField(payload, 'size', company.employee_count_range || fields.company_employee_count_range || fields.employee_count_range);
  addOptionalPayloadField(payload, 'country', locationCountryName(snapshot.location));

  return payload;
}

export function mapLeadPreviewRows(rows = []) {
  return rows.map(mapLeadPreviewRowToCardRecord);
}

export function mapLeadPreviewRowToCardRecord(row) {
  if (row.entity_type === 'company') {
    return mapCompanyRow(row);
  }
  return mapPersonRow(row);
}

function applyPrototypeFilter(filters, filter, entityType) {
  const field = filter.field;
  const values = toValueList(filter.value);

  if (field === 'country') {
    addListValues(filters, 'country_codes', values.map(countryToCode).filter(Boolean));
    return;
  }

  if (field === 'city') {
    addListValues(filters, 'cities', values);
    return;
  }

  if (field === 'industry') {
    addListValues(filters, 'industries', canonicalizeIndustryValues(values));
    return;
  }

  if (field === 'employeeRange') {
    addListValues(filters, 'employee_count_ranges', values);
    return;
  }

  if (field === 'foundedYear' && entityType === 'company') {
    const foundedYearRange = rangeFromFilter(filter);
    if (foundedYearRange) {
      filters.founded_year = unionRanges(filters.founded_year, foundedYearRange);
    }
    return;
  }

  if (field === 'companySize') {
    const companyFilter = ensureCompanyFilter(filters);
    companyFilter.employee_count = unionRanges(companyFilter.employee_count, rangesToNumberRange(values));
    return;
  }

  if (field === 'seniority' && entityType === 'person') {
    addListValues(filters, 'seniorities', values.map(normalizeSeniority).filter(Boolean));
    return;
  }

  if (field === 'role' && entityType === 'person') {
    const functions = values.map(normalizeFunction).filter(Boolean);
    if (functions.length > 0) {
      addListValues(filters, 'functions', functions);
      return;
    }
    addListValues(filters, 'titles_any', values);
    return;
  }

  if (field === 'recentFunding' && entityType === 'company' && filter.value === true) {
    addListValues(filters, 'company_tags_any', ['recently funded']);
    return;
  }

  if (field === 'insights') {
    if (entityType === 'company') {
      addListValues(filters, 'company_tags_any', values);
    } else {
      addListValues(filters, 'highlights_any', values);
    }
  }
}

function parseJsonResponse(response, fallbackMessage) {
  return response.json().then((payload) => {
    if (response.ok) {
      return payload;
    }

    const detail = payload?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      throw new Error(detail);
    }
    throw new Error(fallbackMessage);
  });
}

function mapGuidedSetupResponseToIcp(payload) {
  const icp = payload?.icp;
  if (!icp || typeof icp !== 'object') {
    throw new Error('I received an invalid guided setup response. Please try again.');
  }

  const searchType = icp.search_type === 'companies' ? 'companies' : (icp.search_type === 'people' ? 'people' : null);
  if (!searchType) {
    throw new Error('I received an invalid guided setup response. Please try again.');
  }

  const headline = cleanText(icp.headline);
  const summary = cleanText(icp.summary);
  if (!headline || !summary) {
    throw new Error('I received an invalid guided setup response. Please try again.');
  }

  const facets = Array.isArray(icp.facets) ? icp.facets
    .map((facet) => ({
      key: cleanText(facet?.key),
      label: cleanText(facet?.label),
      value: cleanText(facet?.value),
    }))
    .filter((facet) => facet.key && facet.label && facet.value) : [];
  if (facets.length === 0) {
    throw new Error('I received an invalid guided setup response. Please try again.');
  }

  const suggestedFilters = mapCanonicalFiltersToUi(searchType, icp.suggested_filters || {});
  const suggestedAiColumns = Array.isArray(icp.suggested_ai_columns)
    ? icp.suggested_ai_columns
      .map((column) => ({
        label: cleanText(column?.label),
        prompt: cleanText(column?.prompt),
      }))
      .filter((column) => column.label && column.prompt)
    : [];

  return {
    searchType,
    headline,
    summary,
    facets,
    suggestedFilters,
    suggestedAiColumns,
  };
}

function buildFallbackGuidedIcpPayload(fallbackIcp) {
  const searchType = fallbackIcp?.searchType === 'companies' ? 'companies' : 'people';
  const fallbackFilters = buildCanonicalFiltersFromUiFilters(
    searchType,
    fallbackIcp?.suggestedFilters || []
  );

  return {
    search_type: searchType,
    headline: cleanText(fallbackIcp?.headline),
    summary: cleanText(fallbackIcp?.summary),
    facets: Array.isArray(fallbackIcp?.facets)
      ? fallbackIcp.facets
        .map((facet) => ({
          key: cleanText(facet?.key),
          label: cleanText(facet?.label),
          value: cleanText(facet?.value),
        }))
        .filter((facet) => facet.key && facet.label && facet.value)
      : [],
    suggested_filters: fallbackFilters,
    suggested_ai_columns: Array.isArray(fallbackIcp?.suggestedAiColumns)
      ? fallbackIcp.suggestedAiColumns
        .map((column) => ({
          label: cleanText(column?.label),
          prompt: cleanText(column?.prompt),
        }))
        .filter((column) => column.label && column.prompt)
      : [],
  };
}

async function readErrorMessage(response, fallbackMessage) {
  try {
    const payload = await response.json();
    const detail = payload?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  } catch {
    // Fall through to the caller-provided message for non-JSON failures.
  }
  return fallbackMessage;
}

function addOptionalPayloadField(payload, key, value) {
  const cleaned = cleanText(value);
  if (cleaned) payload[key] = cleaned;
}

function cleanText(value) {
  return String(value || '').trim();
}

function normalizeWebsiteUrl(value) {
  const text = cleanText(value);
  if (!text) return null;
  if (/^https?:\/\//i.test(text)) return text;
  return `https://${text}`;
}

function websiteHostLabel(value) {
  const normalizedUrl = normalizeWebsiteUrl(value);
  if (!normalizedUrl) return null;
  try {
    return new URL(normalizedUrl).hostname.replace(/^www\./i, '');
  } catch {
    return normalizedUrl
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/\/+$/, '');
  }
}

function locationCountryName(location = {}) {
  const country = cleanText(location.country);
  if (country) return country;
  return countryName(location.country_code);
}

function mapPersonRow(row) {
  const fields = row.fields || {};
  const company = row.company || {};
  const name = row.display_name || 'Unknown person';

  return {
    id: row.id,
    name,
    role: fields.title || row.subtitle || 'Unknown role',
    seniority: fields.seniority,
    company: company.display_name || fields.company_name || 'Unknown company',
    companySize: company.employee_count_range || fields.company_employee_count_range,
    industry: company.industry || fields.company_industry,
    country: formatLocation(row.location),
    previousCompanies: toValueList(fields.previous_companies || fields.previousCompanies),
    insights: row.highlights || [],
    availability: row.availability,
    avatarColor: deriveAvatarColor(name),
    initials: initials(name),
    sourceRow: row,
  };
}

function mapCompanyRow(row) {
  const fields = row.fields || {};
  const metrics = row.metrics || {};
  const name = row.display_name || 'Unknown company';
  const websiteUrl = normalizeWebsiteUrl(
    row?.links?.website_url || fields.website_url || fields.website || fields.domain
  );
  const websiteDomain = websiteHostLabel(websiteUrl);

  return {
    id: row.id,
    name,
    industry: fields.industry || row.subtitle || 'Unknown industry',
    employeeRange: fields.employee_count_range || metrics.employee_count_range,
    employeeCount: fields.employee_count || metrics.employee_count,
    country: formatLocation(row.location),
    founded: fields.founded_year || metrics.founded_year,
    insights: row.highlights || [],
    websiteUrl,
    websiteLabel: websiteDomain,
    logoUrl: buildBrandfetchLogoUrl(websiteDomain),
    avatarColor: deriveAvatarColor(name),
    initials: initials(name),
    sourceRow: row,
  };
}

function addListValues(target, key, values) {
  const normalized = values.map((value) => String(value || '').trim()).filter(Boolean);
  if (normalized.length === 0) return;

  const existing = new Set(target[key] || []);
  for (const value of normalized) {
    existing.add(value);
  }
  target[key] = Array.from(existing);
}

function ensureCompanyFilter(filters) {
  if (!filters.company) {
    filters.company = {};
  }
  return filters.company;
}

function unionRanges(existing, next) {
  if (!next) return existing;
  if (!existing) return next;

  return {
    ...(existing.gte !== undefined || next.gte !== undefined
      ? { gte: Math.min(existing.gte ?? next.gte, next.gte ?? existing.gte) }
      : {}),
    ...(existing.lte !== undefined || next.lte !== undefined
      ? { lte: Math.max(existing.lte ?? next.lte, next.lte ?? existing.lte) }
      : {}),
  };
}

function rangesToNumberRange(values) {
  const bounds = values.map((value) => EMPLOYEE_RANGE_BOUNDS[value]).filter(Boolean);
  if (bounds.length === 0) return null;

  const gtes = bounds.map((range) => range.gte).filter((value) => value !== undefined);
  const ltes = bounds.map((range) => range.lte).filter((value) => value !== undefined);

  return {
    ...(gtes.length > 0 ? { gte: Math.min(...gtes) } : {}),
    ...(ltes.length > 0 ? { lte: Math.max(...ltes) } : {}),
  };
}

function rangeFromFilter(filter = {}) {
  const value = filter.value;

  if (filter.op === 'between' && value && typeof value === 'object') {
    const range = {};
    const gte = Number(value.gte);
    const lte = Number(value.lte);
    if (Number.isFinite(gte)) range.gte = gte;
    if (Number.isFinite(lte)) range.lte = lte;
    return Object.keys(range).length > 0 ? range : null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  if (filter.op === 'gte') return { gte: numericValue };
  if (filter.op === 'lte') return { lte: numericValue };
  if (filter.op === 'equals') return { gte: numericValue, lte: numericValue };
  return null;
}

function toValueList(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).filter(Boolean);
  }
  if (value === undefined || value === null || typeof value === 'boolean') {
    return [];
  }
  return [String(value)];
}

function countryToCode(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return COUNTRY_TO_CODE[raw.toLowerCase()] || null;
}

function normalizeSeniority(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return SENIORITY_MAP[normalized] || normalized || null;
}

function normalizeFunction(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return FUNCTION_MAP[normalized] || null;
}

function formatLocation(location = {}) {
  const parts = [location.city, location.region, countryName(location.country_code)]
    .map((part) => String(part || '').trim())
    .filter(Boolean);
  return Array.from(new Set(parts)).join(', ');
}

function countryName(countryCode) {
  const normalized = String(countryCode || '').trim().toUpperCase();
  return COUNTRY_CODE_TO_NAME[normalized] || normalized;
}

function deriveAvatarColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function initials(name) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('');
}
